/**
 * EntitySelector 组件
 * 显示项目和任务的自动完成选择器
 */
import React, { useEffect, useRef, useMemo, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MixedEntity, SearchResult } from '../../../../hooks/v3/useEntityAutocomplete';

interface EntitySelectorProps {
  isOpen: boolean;
  searchResult: SearchResult;
  selectedIndex: number;
  onSelectEntity: (entity: MixedEntity) => void;
  onClose: () => void;
  triggerPosition: { x: number; y: number };
}

/**
 * EntitySelector 组件
 */
export const EntitySelector: React.FC<EntitySelectorProps> = ({
  isOpen,
  searchResult,
  selectedIndex,
  onSelectEntity,
  onClose,
  triggerPosition
}) => {
  const { t } = useTranslation(['v3/chat', 'common']);
  const menuRef = useRef<HTMLDivElement>(null);
  // NOTE: 用于存储选择器的实际高度，以便动态调整位置
  const [actualMenuHeight, setActualMenuHeight] = useState(240);

  // 处理点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // 滚动到选中项
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const selectedElement = menuRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [isOpen, selectedIndex]);

  // 使用 useLayoutEffect 在 DOM 更新后立即测量实际高度
  // NOTE: 当搜索结果变化时，选择器的实际高度会改变，需要重新测量以正确定位
  useLayoutEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setActualMenuHeight(rect.height);
    }
  }, [isOpen, searchResult]);

  // 计算菜单位置
  // NOTE: 使用实际测量的高度而不是固定的最大高度，确保位置计算更准确
  const menuStyle = useMemo(() => {
    const maxWidth = 320;
    const maxHeight = 240;
    // 使用实际高度或最大高度中的较小值
    const effectiveHeight = Math.min(actualMenuHeight, maxHeight);

    // 基础位置
    let left = triggerPosition.x;
    let top = triggerPosition.y + 24; // @ 字符下方一点

    // 防止超出右边界
    if (left + maxWidth > window.innerWidth - 20) {
      left = window.innerWidth - maxWidth - 20;
    }

    // 防止超出左边界
    if (left < 20) {
      left = 20;
    }

    // 防止超出下边界 - 使用实际高度进行计算
    if (top + effectiveHeight > window.innerHeight - 20) {
      top = triggerPosition.y - effectiveHeight - 8; // 显示在 @ 字符上方
    }

    return {
      left: `${left}px`,
      top: `${top}px`,
      maxWidth: `${maxWidth}px`,
      maxHeight: `${maxHeight}px`
    };
  }, [triggerPosition, actualMenuHeight]);

  // 渲染实体项
  const renderEntityItem = (entity: MixedEntity, index: number) => {
    const isSelected = index === selectedIndex;
    const isProject = entity.type === 'project';

    return (
      <button
        key={`${entity.type}-${entity.id}`}
        data-index={index}
        type="button"
        onClick={() => onSelectEntity(entity)}
        className={`w-full text-left px-3 py-2 text-sm flex items-center transition-colors duration-150 ${
          isSelected
            ? 'bg-ios-blue/10 dark:bg-ios-dark-blue/20 text-ios-blue dark:text-ios-dark-blue'
            : 'text-ios-black dark:text-white hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4'
        }`}
      >
        {/* 图标 */}
        <div className="flex-shrink-0 mr-3">
          {isProject ? (
            // 项目图标
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
          ) : (
            // 任务图标
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          )}
        </div>

        {/* 标题和类型 */}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">
            {entity.title}
          </div>
          <div className={`text-xs mt-0.5 ${
            isSelected 
              ? 'text-ios-blue/70 dark:text-ios-dark-blue/70' 
              : 'text-ios-gray dark:text-ios-dark-gray'
          }`}>
            {isProject ? t('common:project.default') : t('common:task')}
          </div>
        </div>
      </button>
    );
  };

  if (!isOpen || searchResult.all.length === 0) {
    return null;
  }

  return (
    <>
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            className="fixed z-[10000] bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray4 flex flex-col"
            style={menuStyle}
          >
            {/* 内容区域 - 为底部提示预留空间 */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(240px - 36px)' }}>
              {/* 搜索结果为空时的提示 */}
              {searchResult.all.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-ios-gray dark:text-ios-dark-gray">
                  {t('v3/chat:entitySelector.noResults', '未找到匹配的项目或任务')}
                </div>
              ) : (
                <>
                  {/* 项目分组 */}
                  {searchResult.projects.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs font-medium text-ios-gray dark:text-ios-dark-gray bg-ios-gray6 dark:bg-ios-dark-gray6">
                        {t('common:projects')} ({searchResult.projects.length})
                      </div>
                      {searchResult.projects.map((entity, index) =>
                        renderEntityItem(entity, index)
                      )}
                    </>
                  )}

                  {/* 任务分组 */}
                  {searchResult.tasks.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs font-medium text-ios-gray dark:text-ios-dark-gray bg-ios-gray6 dark:bg-ios-dark-gray6">
                        {t('common:tasks')} ({searchResult.tasks.length})
                      </div>
                      {searchResult.tasks.map((entity, index) =>
                        renderEntityItem(entity, searchResult.projects.length + index)
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* 底部提示 - 固定在底部，始终可见 */}
            <div className="flex-shrink-0 px-3 py-2 text-xs text-ios-gray dark:text-ios-dark-gray bg-ios-gray6 dark:bg-ios-dark-gray6 border-t border-ios-gray5 dark:border-ios-dark-gray4">
              {t('v3/chat:entitySelector.hint', '↑↓ 选择 • Enter 确认 • Esc 取消')}
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};
